/**
 * Coquille des écrans authentifiés : navigation latérale, en-tête, zone de contenu.
 *
 * Cible A-31 : bureau ≥ 1280 px. La barre latérale fait 240 px et le contenu garde une largeur
 * de lecture confortable ; aucun empilement mobile n'est recherché (le back-office n'est pas
 * utilisé sur téléphone).
 */
import { NavLink } from 'react-router-dom';

import { Bouton } from './Bouton';
import { M } from '../catalogue/messages';
import { useSessionAdmin } from '../session/session-admin';

import type { ReactNode } from 'react';

/** Entrées de navigation, dans l'ordre de travail d'un administrateur. */
const ENTREES = [
  { chemin: '/cartes', libelle: M.nav.cartes, groupe: M.nav.section },
  { chemin: '/equilibrage', libelle: M.nav.equilibrage, groupe: M.nav.section },
  { chemin: '/niveaux', libelle: M.nav.niveaux, groupe: M.nav.section },
  { chemin: '/publication', libelle: M.nav.publication, groupe: M.nav.section },
  { chemin: '/joueurs', libelle: M.nav.joueurs, groupe: M.nav.sectionSuivi },
  { chemin: '/audit', libelle: M.nav.audit, groupe: M.nav.sectionSuivi },
] as const;

export interface ProprietesCoquille {
  readonly titre: string;
  readonly sousTitre?: string;
  readonly actions?: ReactNode;
  readonly children: ReactNode;
}

export function Coquille({ titre, sousTitre, actions, children }: ProprietesCoquille) {
  const admin = useSessionAdmin((etat) => etat.admin);
  const deconnecter = useSessionAdmin((etat) => etat.deconnecter);

  const groupes = [M.nav.section, M.nav.sectionSuivi];

  return (
    <div className="coquille">
      <aside className="coquille__laterale">
        <p className="coquille__marque">{M.app.titre}</p>
        <p className="coquille__marque-sous">{M.app.sousTitre}</p>
        <nav className="coquille__nav" aria-label={M.app.sousTitre}>
          {groupes.map((groupe) => (
            <div className="coquille__groupe" key={groupe}>
              <p className="coquille__groupe-titre">{groupe}</p>
              {ENTREES.filter((entree) => entree.groupe === groupe).map((entree) => (
                <NavLink
                  key={entree.chemin}
                  to={entree.chemin}
                  className={({ isActive }) =>
                    `coquille__lien${isActive ? ' coquille__lien--actif' : ''}`
                  }
                >
                  {entree.libelle}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="coquille__pied">
          <p className="coquille__compte">
            {admin === null ? M.commun.vide : M.app.compte(admin.email)}
          </p>
          <p className="coquille__note">{M.app.jetonMemoire}</p>
          <Bouton variante="discret" onClick={deconnecter}>
            {M.app.deconnexion}
          </Bouton>
        </div>
      </aside>

      <main className="coquille__contenu">
        <header className="entete-ecran">
          <div>
            <h1 className="entete-ecran__titre">{titre}</h1>
            {sousTitre === undefined ? null : (
              <p className="entete-ecran__sous-titre">{sousTitre}</p>
            )}
          </div>
          {actions === undefined ? null : <div className="entete-ecran__actions">{actions}</div>}
        </header>
        {children}
      </main>
    </div>
  );
}
